/**
 * `/notifications` — the account's global alert settings: email, Slack,
 * Discord, and a generic webhook, plus the staleness grace the server checks
 * for on its own hourly cron even when every machine is off.
 *
 * Reached from the profile menu, beside `/account`, and built in the same
 * shell (`AuthCard`) for the same reason `/account` is: it's a settings page
 * about the account, not part of the fleet view under `/dashboard`. A single
 * plan's own override lives on its `JobDetail` page instead — it only makes
 * sense next to the plan it changes, not listed out globally here.
 *
 * Loads once and edits a local copy, the same shape `ChangePasswordForm`
 * uses: nothing here reaches the server until Save is pressed, so a stray
 * keystroke in a webhook URL never goes out on its own.
 */

import {
  DEFAULT_NOTIFICATION_SETTINGS,
  MAX_INTERVAL_DAYS,
  MAX_STALE_AFTER_DAYS,
  MIN_STALE_AFTER_DAYS,
  SUMMARY_CADENCES,
  WEBHOOK_CHANNEL_KINDS,
  channelLabel,
  summaryCadenceHint,
  summaryCadenceLabel,
  webhookUrlProblem,
  type AlertChannelKind,
  type ChannelSettings,
  type NotificationSettings,
  type SummaryCadence,
} from "@bigbear/shared";
import { type FormEvent, useEffect, useState } from "react";

import type { PortalApi, TestableChannelKind } from "../api/portal";
import { AuthCard } from "./auth/AuthCard";

const TESTABLE_CHANNEL_KINDS: readonly TestableChannelKind[] =
  WEBHOOK_CHANNEL_KINDS;

/**
 * The cadence the "Preview summary" button renders.
 *
 * There is one preview for the whole page rather than one per channel,
 * because the summary is the same report everywhere — only the wrapping
 * differs. It follows the first channel actually asking for a summary, so the
 * preview shows what this account has set up rather than a default nobody
 * chose, and falls back to weekly when nothing is enabled yet.
 */
function previewCadence(
  settings: NotificationSettings,
): Exclude<SummaryCadence, "off"> {
  for (const kind of ALL_KINDS) {
    const channel = settings.channels[kind];
    const cadence = channel.summaryCadence ?? "weekly";
    if (channel.enabled && cadence !== "off") return cadence;
  }
  return "weekly";
}

const ALL_KINDS: readonly AlertChannelKind[] = [
  "email",
  ...WEBHOOK_CHANNEL_KINDS,
];

/**
 * A day count from a number input, clamped, with a fallback for the values
 * `Number` turns into something nonsensical.
 *
 * `Number("")` is 0, which is why this exists: clearing the "at most every N
 * days" box read as a deliberate 0, and 0 is Instant. The control switched
 * mode mid-edit, unmounted the input being typed in, and left the remembered
 * draft at 0 — after which the frequency select could not be moved off Instant
 * without reloading the page, and Save wrote the wrong mode. The desktop
 * dialog's `dayCount` has clamped this all along; this is the same guard.
 */
function dayCount(
  raw: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const parsed = Math.round(Number(raw));
  if (!Number.isFinite(parsed) || raw.trim() === "") return fallback;
  return Math.min(Math.max(parsed, min), max);
}

interface Props {
  api: PortalApi;
}

export function Notifications({ api }: Props) {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .notificationSettings()
      .then((loaded) => {
        if (active) setSettings(loaded);
      })
      .catch((cause: unknown) => {
        if (active) setLoadError(explain(cause));
      });
    return () => {
      active = false;
    };
  }, [api]);

  return (
    <AuthCard title="Notification settings" media={null}>
      <p className="muted auth__intro">
        Get an alert by email, Slack, Discord, or a webhook when a scheduled
        backup fails, or when a plan goes quiet for too long. Manual runs never
        trigger these — only unattended ones do.
      </p>

      {loadError && (
        <p className="auth__error" role="alert">
          {loadError}
        </p>
      )}

      {!settings && !loadError && <p className="muted">Loading…</p>}

      {settings && <NotificationsForm api={api} initial={settings} />}
    </AuthCard>
  );
}

function NotificationsForm({
  api,
  initial,
}: {
  api: PortalApi;
  initial: NotificationSettings;
}) {
  const [settings, setSettings] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  function updateChannel(
    kind: AlertChannelKind,
    patch: Partial<ChannelSettings>,
  ) {
    setSettings((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [kind]: { ...prev.channels[kind], ...patch },
      },
    }));
    setSaved(false);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    for (const kind of WEBHOOK_CHANNEL_KINDS) {
      const channel = settings.channels[kind];
      if (!channel.enabled) continue;
      const problem = webhookUrlProblem(channel.webhookUrl ?? "");
      if (problem) {
        setError(`${channelLabel(kind)}: ${problem}`);
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Sent on every save rather than edited anywhere: "weekly" has to mean
      // Monday morning where the person is, and the browser already knows
      // which zone that is. A browser that cannot answer omits the field
      // rather than sending null — the server reads null as "clear it", and
      // clearing a zone the desktop app knew is strictly worse than keeping it.
      const zone = browserTimeZone();
      const result = await api.saveNotificationSettings(
        zone ? { ...settings, timeZone: zone } : settings,
      );
      setSettings(result);
      setSaved(true);
    } catch (cause) {
      setError(explain(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      {saved && (
        <p className="auth__notice" role="status">
          Saved.
        </p>
      )}
      {error && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      <div className="field">
        <label htmlFor="notify-stale-after-days">
          Warn after N days without a successful backup
        </label>
        <input
          id="notify-stale-after-days"
          type="number"
          min={MIN_STALE_AFTER_DAYS}
          max={MAX_STALE_AFTER_DAYS}
          value={settings.staleAfterDays}
          onChange={(event) => {
            const staleAfterDays = dayCount(
              event.target.value,
              DEFAULT_NOTIFICATION_SETTINGS.staleAfterDays,
              MIN_STALE_AFTER_DAYS,
              MAX_STALE_AFTER_DAYS,
            );
            setSettings((prev) => ({ ...prev, staleAfterDays }));
            setSaved(false);
          }}
        />
        <span className="field__hint muted">
          The server checks every hour, whether or not the machine is on, and
          alerts once a plan has gone this long past its usual schedule.
        </span>
      </div>

      <EmailChannelFields
        api={api}
        channel={settings.channels.email}
        onChange={(patch) => updateChannel("email", patch)}
      />

      {TESTABLE_CHANNEL_KINDS.map((kind) => (
        <WebhookChannelFields
          key={kind}
          api={api}
          kind={kind}
          channel={settings.channels[kind]}
          onChange={(patch) => updateChannel(kind, patch)}
        />
      ))}

      <button
        type="submit"
        className="button button--primary auth__submit"
        disabled={busy}
      >
        {busy ? "Saving…" : "Save"}
      </button>

      <p className="notify-preview__link">
        <button
          type="button"
          className="button button--small"
          onClick={() => setPreviewing(true)}
        >
          Preview summary
        </button>
      </p>

      {previewing && (
        <SummaryPreviewModal
          api={api}
          cadence={previewCadence(settings)}
          onClose={() => setPreviewing(false)}
        />
      )}
    </form>
  );
}

function EmailChannelFields({
  api,
  channel,
  onChange,
}: {
  api: PortalApi;
  channel: ChannelSettings;
  onChange: (patch: Partial<ChannelSettings>) => void;
}) {
  return (
    <section className="notify-channel">
      <label className="notify-channel__enable">
        <input
          type="checkbox"
          checked={channel.enabled}
          onChange={(event) => onChange({ enabled: event.target.checked })}
        />
        Email
      </label>

      {channel.enabled && (
        <div className="notify-channel__body">
          <div className="field">
            <label htmlFor="notify-email-interval-days">
              At most every N days
            </label>
            <input
              id="notify-email-interval-days"
              type="number"
              min={1}
              max={MAX_INTERVAL_DAYS}
              value={channel.minIntervalDays}
              onChange={(event) =>
                onChange({
                  minIntervalDays: dayCount(
                    event.target.value,
                    1,
                    1,
                    MAX_INTERVAL_DAYS,
                  ),
                })
              }
            />
            <span className="field__hint muted">
              Emails are capped to protect against noise — even a plan failing
              every hour sends at most one a day.
            </span>
          </div>

          <SummaryFields
            api={api}
            kind="email"
            channel={channel}
            onChange={onChange}
          />
        </div>
      )}
    </section>
  );
}

type TestSendState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "success" }
  | { status: "error"; message: string };

function WebhookChannelFields({
  api,
  kind,
  channel,
  onChange,
}: {
  api: PortalApi;
  kind: TestableChannelKind;
  channel: ChannelSettings;
  onChange: (patch: Partial<ChannelSettings>) => void;
}) {
  const isInstant = channel.minIntervalDays === 0;
  // Remembered locally rather than in `channel` itself: switching to Instant
  // sets `minIntervalDays` to 0, and without a place to keep the number the
  // form last showed, switching back would always land on 1 instead of
  // wherever the visitor had actually left it.
  const [intervalDraft, setIntervalDraft] = useState(
    channel.minIntervalDays > 0 ? channel.minIntervalDays : 1,
  );
  const [test, setTest] = useState<TestSendState>({ status: "idle" });

  function onFrequencyChange(value: string) {
    if (value === "instant") {
      onChange({ minIntervalDays: 0 });
    } else {
      // Only honoured in Instant mode — leaving it would silently disagree
      // with the checkbox this same change just disabled.
      onChange({ minIntervalDays: intervalDraft, notifyOnSuccess: false });
    }
  }

  function onIntervalDaysChange(value: number) {
    setIntervalDraft(value);
    onChange({ minIntervalDays: value });
  }

  async function sendTest() {
    const url = channel.webhookUrl ?? "";
    const problem = webhookUrlProblem(url);
    if (problem) {
      setTest({ status: "error", message: problem });
      return;
    }

    setTest({ status: "sending" });
    try {
      await api.sendNotificationTest(kind, url);
      setTest({ status: "success" });
    } catch (cause) {
      setTest({ status: "error", message: explain(cause) });
    }
  }

  const urlId = `notify-${kind}-url`;
  const frequencyId = `notify-${kind}-frequency`;
  const intervalId = `notify-${kind}-interval-days`;

  return (
    <section className="notify-channel">
      <label className="notify-channel__enable">
        <input
          type="checkbox"
          checked={channel.enabled}
          onChange={(event) => onChange({ enabled: event.target.checked })}
        />
        {channelLabel(kind)}
      </label>

      {channel.enabled && (
        <div className="notify-channel__body">
          <div className="field">
            <label htmlFor={urlId}>Webhook URL</label>
            <input
              id={urlId}
              type="url"
              value={channel.webhookUrl ?? ""}
              onChange={(event) => onChange({ webhookUrl: event.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor={frequencyId}>Frequency</label>
            <select
              id={frequencyId}
              value={isInstant ? "instant" : "interval"}
              onChange={(event) => onFrequencyChange(event.target.value)}
            >
              <option value="instant">Instant</option>
              <option value="interval">Every N days</option>
            </select>
          </div>

          {!isInstant && (
            <div className="field">
              <label htmlFor={intervalId}>Days between alerts</label>
              <input
                id={intervalId}
                type="number"
                min={1}
                max={MAX_INTERVAL_DAYS}
                value={channel.minIntervalDays}
                onChange={(event) =>
                  onIntervalDaysChange(
                    dayCount(event.target.value, 1, 1, MAX_INTERVAL_DAYS),
                  )
                }
              />
            </div>
          )}

          <label className="field--checkbox">
            <input
              type="checkbox"
              checked={channel.notifyOnSuccess === true}
              disabled={!isInstant}
              onChange={(event) =>
                onChange({ notifyOnSuccess: event.target.checked })
              }
            />
            Also ping on success
          </label>
          <p className="field__hint muted">
            Only available in Instant mode — a success ping delivered days late
            is just noise.
          </p>

          <SummaryFields
            api={api}
            kind={kind}
            channel={channel}
            onChange={onChange}
          />

          <div className="notify-channel__test">
            <button
              type="button"
              className="button button--small"
              onClick={sendTest}
              disabled={test.status === "sending"}
            >
              {test.status === "sending" ? "Sending…" : "Send test"}
            </button>
            {test.status === "success" && (
              <span className="notify-channel__test-result--ok" role="status">
                Test sent.
              </span>
            )}
            {test.status === "error" && (
              <span className="notify-channel__test-result--error" role="alert">
                {test.message}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * The cadence select and the "Send test summary" button, identical for every
 * channel because the summary is: only the envelope differs, and a person
 * choosing "weekly" on Slack is choosing the same report they would get by
 * email.
 *
 * Unlike the alert test beside it, this one sends to the channel as it is
 * **saved** — the server reads the URL from the account, not from the page —
 * so it says so, rather than letting someone type a URL, click test, and
 * conclude a URL they never saved is working.
 */
function SummaryFields({
  api,
  kind,
  channel,
  onChange,
}: {
  api: PortalApi;
  kind: AlertChannelKind;
  channel: ChannelSettings;
  onChange: (patch: Partial<ChannelSettings>) => void;
}) {
  const [test, setTest] = useState<TestSendState>({ status: "idle" });
  const cadence = channel.summaryCadence ?? "weekly";
  const cadenceId = `notify-${kind}-summary`;

  async function sendTest() {
    if (cadence === "off") return;
    setTest({ status: "sending" });
    try {
      await api.sendSummaryTest(kind, cadence);
      setTest({ status: "success" });
    } catch (cause) {
      setTest({ status: "error", message: explain(cause) });
    }
  }

  return (
    <>
      <div className="field">
        <label htmlFor={cadenceId}>Backup summary</label>
        <select
          id={cadenceId}
          value={cadence}
          onChange={(event) => {
            onChange({ summaryCadence: event.target.value as SummaryCadence });
            setTest({ status: "idle" });
          }}
        >
          {SUMMARY_CADENCES.map((option) => (
            <option key={option} value={option}>
              {summaryCadenceLabel(option)}
            </option>
          ))}
        </select>
        <span className="field__hint muted">{summaryCadenceHint(cadence)}</span>
      </div>

      {cadence !== "off" && (
        <div className="notify-channel__test">
          <button
            type="button"
            className="button button--small"
            onClick={sendTest}
            disabled={test.status === "sending"}
          >
            {test.status === "sending" ? "Sending…" : "Send test summary"}
          </button>
          {test.status === "success" && (
            <span className="notify-channel__test-result--ok" role="status">
              Summary sent.
            </span>
          )}
          {test.status === "error" && (
            <span className="notify-channel__test-result--error" role="alert">
              {test.message}
            </span>
          )}
        </div>
      )}
    </>
  );
}

/**
 * The rendered summary email, in a sandboxed iframe.
 *
 * `sandbox=""` with no tokens: the document is built by our own worker, but it
 * is built out of plan and machine names that came from someone's machine, and
 * an email body has no business running anything. `srcDoc` rather than a URL
 * keeps it out of the network tab and off any origin of ours.
 */
function SummaryPreviewModal({
  api,
  cadence,
  onClose,
}: {
  api: PortalApi;
  cadence: Exclude<SummaryCadence, "off">;
  onClose: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .summaryPreview(cadence)
      .then((preview) => {
        if (active) setHtml(preview.html);
      })
      .catch((cause: unknown) => {
        if (active) setError(explain(cause));
      });
    return () => {
      active = false;
    };
  }, [api, cadence]);

  return (
    <div
      className="notify-preview"
      role="dialog"
      aria-modal="true"
      aria-label="Summary preview"
    >
      <div className="notify-preview__panel">
        <div className="notify-preview__head">
          <strong>{summaryCadenceLabel(cadence)} summary</strong>
          <button
            type="button"
            className="button button--small"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {error && (
          <p className="auth__error" role="alert">
            {error}
          </p>
        )}
        {!html && !error && <p className="muted">Building a preview…</p>}
        {html && (
          <iframe
            className="notify-preview__frame"
            sandbox=""
            srcDoc={html}
            title="Summary preview"
          />
        )}
      </div>
    </div>
  );
}

/**
 * The browser's IANA zone, or `null` where it cannot be read.
 *
 * `null` is not "UTC": it means nothing was learned here, so the field is left
 * off the save entirely and the account keeps whatever it already had — which
 * may well be a zone the desktop app sent from the same account's machine.
 */
function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function explain(cause: unknown): string {
  return cause instanceof Error
    ? cause.message
    : "Could not reach the server. Please try again.";
}
